import React, { useEffect, useMemo, useRef, useState } from 'react';

import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ReactComponent as AlertIcon } from '../../../../assets/icons/v3/alert.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/v3/close.svg';
import { ReactComponent as ErrorIcon } from '../../../../assets/icons/v3/error.svg';
import { ICollaboratorsEmailData } from '../../../interfaces/collaborators';
import { TRANSLATIONS_GLOBAL } from '../../../translations/commons';
import { addEmailInState, handleChange, handleOnPaste } from '../../../utils/emailList/emailList';
import { validateEmail } from '../../../utils/validation/validation';

import './ManageEmailsContainer.scss';

interface IManageEmailsContainer {
  errorEmails: string[];
  hideNonErrorTags?: boolean;
  handleChangeEmails: (emails: ICollaboratorsEmailData[], errorEmails: string[]) => void;
  loading?: boolean;
  type?: 'delete-subscriptions' | 'upload-collaborators';
  reset?: boolean;
  initialEmails?: ICollaboratorsEmailData[];
}

const ManageEmailsContainer = ({
  errorEmails,
  handleChangeEmails,
  loading = false,
  hideNonErrorTags = false,
  reset = false,
  initialEmails,
  type = 'delete-subscriptions'
}: IManageEmailsContainer) => {
  const [value, setValue] = useState('');
  const [emails, setEmails] = useState<ICollaboratorsEmailData[]>([]);
  const [emailsWithError, setEmailsWithError] = useState<ICollaboratorsEmailData[]>([]);
  const [edittingEmail, setEdittingEmail] = useState<number | null>(null);
  const [emailCount, setEmailCount] = useState(0);
  const [initialEmailsLoaded, setInitialEmailsLoaded] = useState(false);

  const chipInputRef = useRef<(HTMLInputElement | null)[]>([]);
  const chipContainerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const keysToPress = ['Enter', 'Tab', ' ', ','];

  const placeholderText = useMemo(() => {
    return emails.length == 0 ? t(TRANSLATIONS_GLOBAL.modals.inviteCollaboratorsTextArea.placeholder) : '';
  }, [emails]);

  const handleKeyDown = (
    evt: React.KeyboardEvent<HTMLInputElement> | React.KeyboardEvent<HTMLTextAreaElement>,
    isInput = false
  ) => {
    const keysToHandle = isInput ? keysToPress.concat('x') : keysToPress;
    if (keysToHandle.includes(evt.key) && (['Enter', 'Tab', ' '].includes(evt.key) || evt.ctrlKey)) {
      evt.preventDefault();
      setEdittingEmail(null);
      const emailVar = value.trim().toLowerCase();
      if (emailVar) {
        addEmailInState(emails, emailVar, setEmails, setValue);
      }
      if (chipContainerRef.current) {
        chipContainerRef.current.scrollTop = chipContainerRef.current.scrollHeight;
      }
    }
  };

  const handleEditEmail = (email: string, index: number) => {
    const updatedEmails = [...emails];
    const oldEmail = updatedEmails[index].emailString;
    updatedEmails[index] = {
      emailString: email,
      isValidEmail: validateEmail(email, true),
      id: updatedEmails[index].id
    };

    const inputElement = chipInputRef.current[edittingEmail ?? index];
    const handleKeyDown = (e: KeyboardEvent) => {
      if (keysToPress.includes(e.key)) {
        setEmails(updatedEmails);
        const updatedErrorEmails = errorEmails.filter((e) => e !== oldEmail);
        handleChangeEmails(updatedEmails, updatedErrorEmails);
        inputElement?.removeEventListener('keydown', handleKeyDown);
      }
    };

    inputElement?.addEventListener('keydown', handleKeyDown);
  };

  const getErrorClassname = (id: string) => {
    const email = emails.find((email) => email.id === id);
    const hasError = emailsWithError.some((errorEmail) => errorEmail.id === id);

    if (type === 'delete-subscriptions' && hasError && errorEmails.includes(email?.emailString || '')) {
      return 'email-not-found';
    }
    if (!email?.isValidEmail) {
      return 'invalid-email';
    }
    if (hideNonErrorTags && !hasError) {
      return 'not-diplay';
    }
    return '';
  };

  const removeEmail = (emailToRemove: string) => {
    setEmails(emails.filter((email) => email.emailString !== emailToRemove));
    setEmailsWithError(emailsWithError.filter((email) => email.emailString !== emailToRemove));
    handleChangeEmails(
      emails.filter((email) => email.emailString !== emailToRemove),
      errorEmails.filter((email) => email !== emailToRemove)
    );
  };

  useEffect(() => {
    handleChangeEmails(emails, errorEmails);
  }, [emails, errorEmails, handleChangeEmails]);

  useEffect(() => {
    setEmailsWithError(emails.filter((email) => errorEmails.includes(email.emailString)));
  }, [emails, errorEmails]);

  useEffect(() => {
    if (reset) {
      setEmails([]);
      setValue('');
      setEmailsWithError([]);
    }
  }, [reset]);

  useEffect(() => {
    if (edittingEmail !== null && chipInputRef.current[edittingEmail]) {
      chipInputRef.current[edittingEmail]?.focus();
    }
  }, [edittingEmail]);

  useEffect(() => {
    if (chipContainerRef.current && emails.length > emailCount) {
      chipContainerRef.current.scrollTop = chipContainerRef.current.scrollHeight;
    }
    setEmailCount(emails.length);
  }, [emails]);

  useEffect(() => {
    if (type === 'upload-collaborators' && initialEmails && initialEmails.length > 0 && !initialEmailsLoaded) {
      setEmails(initialEmails);
      setInitialEmailsLoaded(true);
    }
  }, [initialEmails, type, initialEmailsLoaded]);

  if (loading) {
    return (
      <div className="manage-email-container">
        <div className="loading-container">
          <CircularProgress size={32} />
        </div>
      </div>
    );
  }

  return (
    <div className="manage-email-container" ref={chipContainerRef}>
      <div className="email-chips">
        {emails.map((emailData: ICollaboratorsEmailData, index) =>
          edittingEmail == index ? (
            <input
              key={emailData.id}
              ref={(ref) => (chipInputRef.current[index] = ref)}
              className="input-chip"
              defaultValue={emailData.emailString}
              onChange={(e) => handleEditEmail(e.target.value, index)}
              onBlur={() => setEdittingEmail(null)}
              onKeyDown={(e) => handleKeyDown(e, true)}
            />
          ) : (
            <div className="tag-item-plus-error" key={emailData.id}>
              <div className={`tag-item ${type} ${getErrorClassname(emailData.id)}`}>
                {!emailData.isValidEmail && <ErrorIcon className="tag-icon" />}
                {errorEmails.includes(emailData.emailString) && <AlertIcon className="tag-icon" />}
                <button
                  key={emailData.id}
                  className={
                    emailData.isValidEmail && !errorEmails.includes(emailData.emailString) ? 'email' : 'email error'
                  }
                  onClick={() => setEdittingEmail(index)}
                >
                  {emailData.emailString}
                </button>
                <div className="close-icon">
                  <CloseIcon
                    className="tag-icon close"
                    onClick={() => removeEmail(emailData.emailString)}
                    title="close"
                  />
                </div>
              </div>
              {!emailData.isValidEmail && (
                <div className="error-message format">
                  {t(TRANSLATIONS_GLOBAL.modals.inviteCollaboratorsTextAreaV3.emailFormatError)}
                </div>
              )}
            </div>
          )
        )}
        <input
          value={value}
          placeholder={placeholderText}
          onKeyDown={handleKeyDown}
          onChange={(e) => handleChange(e, setValue)}
          onPaste={(e) => handleOnPaste(e, emails, setEmails)}
        />
      </div>
    </div>
  );
};

export default ManageEmailsContainer;
